import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  saveButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#993d13",
    },
  },
  backButton: {
    backgroundColor: "#707070",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    width: "12rem",
    fontSize: "1rem",
    fontWeight: 550,
    cursor: "pointer",
    paddingTop: "0.35rem",
    paddingBottom: "0.35rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5rem",
    "&:hover": {
      backgroundColor: "#4d4d4d",
    },
  },
}));

const DocumentGroupTab = (props) => {
  const { addSponsorList, rowData, rowNetwork, updateNetworkData } = props;
  const classes = useStyles();
  const [documentGroups, setDocumentGroups] = useState([]); // Local state like PO Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingStates, setSavingStates] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const network = addSponsorList?.data?.network || rowNetwork;
  const networkUrl = updateNetworkData?.ecosystemUrl || rowData?.networkURL;

  const fetchDocumentGroups = async () => {
    setLoading(true);
    setError("");
    console.log("Fetching document groups...");
    try {
      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/fetchDocumentGroups/false`; // API endpoint with false to show all (true and false activated)
      
      const response = await axios.get(url);
      
      if (response.data?.status === true || response.data?.status === "true") {
        
        // Check if documentGroups exists and is an array
        const documentGroupList = response.data?.data?.documentGroups || response.data?.documentGroups || [];
        
        // Map the API response to match the expected structure
        const mappedDocumentGroups = documentGroupList.map((doc) => ({
          id: doc.documentGroupID, // Map documentGroupID to id
          name: doc.documentGroupName, // Map documentGroupName to name
          selected: !!doc.activated, // Map activated to selected, convert to boolean (shows both true and false)
          isNew: false,
          type: doc.documentGroupType, // Map documentGroupType to type
          lastUpdatedBy: doc.lastUpdatedBy, // Keep lastUpdatedBy info
        }));

        setDocumentGroups(mappedDocumentGroups); // Update state with mapped document groups
      } else {
        console.error("Failed to fetch document groups:", response.data?.message);
        setError(response.data?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching document groups:", error);
      console.error("Error details:", error.response?.data);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (networkUrl && network) {
      fetchDocumentGroups(); // Fetch document groups on component mount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: Fetching is also handled by parent Tabs.jsx
  // This function is used for initial load and refreshing after add/delete operations

  const addDocumentGroup = async (index) => {
    if (savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const groupToAdd = documentGroups[index]; // Get the document group to save
      if (!groupToAdd.name.trim()) {
        setError("Please provide a name for the document group.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      // Prepare the payload for the API
      const payload = {
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        documentGroups: [
          {
            documentGroupName: groupToAdd.name.trim(), // Name of the document group
            documentGroupType: "New", // Type of the document group
            activated: groupToAdd.selected, // Use the selected state (toggle value)
          },
        ],
      };

      console.log("Saving document group with payload:", payload);

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/addDocumentGroups`; // API endpoint
      const response = await axios.post(url, payload);
      
      if (response.data?.status === true || response.data?.status === "true") {
        fetchDocumentGroups(); // Refresh the groups list (this will increase the count)
        if (props.setDocumentGroup) {
          props.setDocumentGroup((prev) => !prev);
        }
        // Update the state to mark the document group as saved
        setDocumentGroups((prev) =>
          prev.map((doc, i) =>
            i === index
              ? { ...doc, id: response.data?.data?.id, isNew: false, type: "New" }
              : doc
          )
        );
      } else {
        console.error("Failed to save document group:", response.data?.message);
        setError(response.data?.message || "Failed to save document group.");
        // Remove unsaved item on failure
        setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
      }
    } catch (error) {
      console.error("Error saving document group:", error);
      setError("An error occurred while saving the document group.");
      // Remove unsaved item on error
      setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleNextClick = async () => {
    try {
      const isAnyGroupSelected = documentGroups.filter(
        (group) => group.isUpdated
      );
      if (!isAnyGroupSelected.length) {
        props.handleNext();
        return;
      }
      if (documentGroups.every((group) => !group.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }

      setIsSaving(true);

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        documentGroups: isAnyGroupSelected.map((group) => ({
          documentGroupID: group.id,
          documentGroupName: group.name?.trim() || group.name,
          documentGroupType: group.type,
          activated: group.selected,
          markForDelete: false,
        })),
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateDocumentGroups`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        await fetchDocumentGroups();
        if (props.handleNext) {
          props.handleNext();
        }
      } else {
        console.error("Failed to update document groups:", response.data?.message);
        setErrorMessage(response.data?.message || "Failed to update document groups.");
      }
    } catch (error) {
      console.error("Error updating document groups:", error);
      setErrorMessage("An error occurred while updating document groups. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      const groupToDelete = documentGroups[index];

      if (groupToDelete.isNew) {
        setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      const payload = {
        ecosystemId: addSponsorList?.data?.ecosystemId || rowData?.ecosystemId,
        sponsorId: addSponsorList?.data?.organizationId || rowData?.organizationId,
        documentGroups: [
          {
            documentGroupID: groupToDelete.id,
            documentGroupName: groupToDelete.name?.trim() || groupToDelete.name,
            documentGroupType: groupToDelete.type,
            activated: groupToDelete.selected,
            markForDelete: true,
          },
        ],
      };

      const url = `${networkUrl}/Pharma%20Contract%20Manufacturing/${network}/Pharma%20Co/Admin/updateDocumentGroups`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        await fetchDocumentGroups();
        props.setDocumentGroup && props.setDocumentGroup((prev) => !prev);
        setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error("Failed to delete Document Group:", response.data?.message);
        setError(response.data?.message || "Failed to delete Document Group.");
      }
    } catch (error) {
      console.error("Error deleting Document Group:", error);
      setError("An error occurred while deleting the Document Group.");
    }
  };

  const handleToggle = (index) => {
    const updated = [...documentGroups];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setDocumentGroups(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...documentGroups];
    updated[index].name = value;
    setDocumentGroups(updated);
  };

  const handleAddStatus = () => {
    setDocumentGroups([
      ...documentGroups,
      {
        name: "",
        selected: false,
        isNew: true,
        type: "New",
      },
    ]);
    // Don't set isUpdated here - count should only increase after saving
  };

  const handleCancel = (index) => {
    const groupToCancel = documentGroups[index];

    if (groupToCancel.isNew) {
      setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setDocumentGroups((prev) =>
      prev.map((group, i) =>
        i === index
          ? {
              ...group,
              isEditing: false,
              name: group.originalName || group.name,
            }
          : group
      )
    );
  };

  return (
    <div className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Selection</TableCell>
            <TableCell className={classes.headerCell}>Document Group</TableCell>
            <TableCell className={classes.headerCell}>Document Category ID</TableCell>
            <TableCell className={classes.headerCell}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress />
                </div>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    backgroundColor: "#ffecec",
                    color: "#b00020",
                    border: "1px solid #ff9999",
                  }}
                >
                  <Typography>{error}</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : documentGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "60px",
                    color: "#666",
                  }}
                >
                  <Typography>No Document Groups Found</Typography>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            documentGroups.map((item, index) => (
              <TableRow key={item.id || index}>
                <TableCell className={classes.bodyCell}>
                  <Switch
                    checked={item.selected}
                    onChange={() => handleToggle(index)}
                    classes={{ switchBase: classes.switchBase }}
                  />
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {item.isNew ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TextField
                        placeholder="Type Text Here"
                        value={item.name}
                        onChange={(e) =>
                          handleNameChange(index, e.target.value)
                        }
                        className={classes.textField}
                        variant="outlined"
                        inputProps={{
                          onBeforeInput: (e) => {
                            const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                            if (!allowedChars.test(e.data)) {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                      <div className={classes.inlineActions}>
                        <IconButton
                          onClick={() => handleCancel(index)}
                          className={classes.iconBtn}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => addDocumentGroup(index)}
                          className={classes.iconBtn}
                          disabled={
                            !item.name?.trim() ||
                            documentGroups.some(
                              (s, i) =>
                                i !== index &&
                                s.name?.trim().toLowerCase() === item.name?.trim().toLowerCase()
                            ) || savingStates[index]
                          }
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    item.name
                  )}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {item.isNew ? "" : item.id}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {(item.isNew == true || item.type === "New") && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon className={classes.deleteIcon} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}

      <Button
        variant="outlined"
        onClick={handleAddStatus}
        className={classes.addButton}
      >
        <span className={classes.addIconCircle}>+</span>
        Add New Document Group
      </Button>

      <div className={classes.footer}>
        <Grid
          className={classes.backButton}
          onClick={() => {
            props.handleBack();
          }}
        >
          <ChevronLeftIcon />
          Back
        </Grid>
        <Grid
          className={classes.saveButton}
          onClick={() => {
            if (isUpdated) {
              handleNextClick();
            } else {
              props.handleNext();
            }
          }}
          style={{ 
            opacity: isSaving ? 0.7 : 1, 
            pointerEvents: isSaving ? 'none' : 'auto' 
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: '8px' }} />
              Saving...
            </>
          ) : (
            <>
              Save & Next
              <ChevronRightIcon />
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default DocumentGroupTab;

